import React from 'react';
import useGameStore from '../../store/gameStore';
import { formatMoney } from '../../utils/format';
import { CreditCard, X, Check, AlertCircle } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { BANKS } from '../../data/bankData';

/**
 * Confirmation modal for early loan repayment.
 * Indemnity = min(3% of remaining principal, 6 months of contractual interest).
 */
export const EarlyRepayLoanModal = ({ loan, onClose }) => {
  const { t } = useTranslation();
  const computeEarlyRepayQuote = useGameStore(s => s.computeEarlyRepayQuote);
  const earlyRepayLoan = useGameStore(s => s.earlyRepayLoan);
  const treasury = useGameStore(s => s.treasury);

  const quote = computeEarlyRepayQuote(loan.id);
  if (!quote) return null;
  const canAfford = treasury >= quote.total;
  const projectedTreasury = treasury - quote.total;
  const bank = loan.bankId ? BANKS[loan.bankId] : null;

  const handleConfirm = () => {
    const result = earlyRepayLoan(loan.id);
    if (result) onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      data-testid="early-repay-overlay"
    >
      <div
        className="bg-ink-800 rounded-xl w-full max-w-md shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 duration-200"
        data-testid="early-repay-modal"
      >
        <div className="px-5 py-3 flex items-center justify-between" style={{ backgroundColor: bank?.color || '#1F3864' }}>
          <div className="flex items-center gap-2 text-white">
            <CreditCard className="w-5 h-5" />
            <h3 className="font-bold text-base">{t('early_repay_title')}</h3>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="close-early-repay-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {bank && (
            <div className="flex items-center gap-2">
              <img src={bank.advisor.portrait} alt="" className="w-8 h-8 rounded-full object-cover object-top border-2" style={{ borderColor: bank.color }} />
              <div>
                <p className="text-sm font-semibold" style={{ color: bank.color }}>{bank.name}</p>
                <p className="text-[10px] text-slate-500">{(loan.annualRate * 100).toFixed(2)}% · {loan.remainingMonths} {t('months')}</p>
              </div>
            </div>
          )}

          <p className="text-[12px] text-slate-400 leading-snug">{t('early_repay_intro')}</p>

          <div className="rounded-md border border-ink-600 bg-ink-700/30 divide-y divide-ink-600 text-xs">
            <div className="flex items-center justify-between px-3 py-2">
              <span className="text-slate-400">{t('early_repay_remaining')}</span>
              <span className="font-bold text-slate-200" data-testid="early-repay-principal">{formatMoney(quote.principal)}</span>
            </div>
            <div className="flex items-center justify-between px-3 py-2">
              <span className="text-slate-400">{t('early_repay_indemnity')}</span>
              <span className="font-bold text-danger" data-testid="early-repay-indemnity">+ {formatMoney(quote.indemnity)}</span>
            </div>
            <div className="flex items-center justify-between px-3 py-2.5" style={{ background: (bank?.color || '#1F3864') + '15' }}>
              <span className="text-sm font-semibold" style={{ color: bank?.color || '#1F3864' }}>{t('early_repay_total')}</span>
              <span className="text-base font-black" style={{ color: bank?.color || '#1F3864' }} data-testid="early-repay-total">{formatMoney(quote.total)}</span>
            </div>
          </div>

          <div className="rounded-md border border-ink-600 bg-ink-700/30 p-2.5 text-xs">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">{t('treasury')} (post-remboursement)</p>
            <p className={`text-base font-bold mt-0.5 ${projectedTreasury < 0 ? 'text-danger' : 'text-money'}`} data-testid="early-repay-projected-treasury">
              {formatMoney(projectedTreasury)}
            </p>
          </div>

          {!canAfford && (
            <div className="rounded-md border border-danger/40 bg-danger/5 px-3 py-2 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-danger flex-shrink-0" />
              <p className="text-[12px] text-danger">{t('early_repay_insufficient')}</p>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-ink-600 bg-ink-700/30 flex items-center justify-between">
          <button
            onClick={onClose}
            className="text-xs text-slate-400 hover:text-slate-200 px-3 py-2 transition-colors"
            data-testid="cancel-early-repay-btn"
          >
            {t('cancel')}
          </button>
          <button
            onClick={handleConfirm}
            disabled={!canAfford}
            className="text-white px-4 py-2 rounded-lg text-sm font-bold transition-colors flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90"
            style={{ backgroundColor: bank?.color || '#1F3864' }}
            data-testid="confirm-early-repay-btn"
          >
            <Check className="w-4 h-4" />
            {t('early_repay_confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EarlyRepayLoanModal;
